import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Inline label rendered to the right of the box. */
  label?: ReactNode;
  /** Optional helper line below the label. */
  hint?: ReactNode;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `chk-${reactId}`;
  return (
    <label
      htmlFor={fieldId}
      className={cn(
        'group inline-flex items-start gap-2.5 text-ink-700 cursor-pointer select-none',
        className,
      )}
    >
      <span className="relative w-[16px] h-[16px] rounded-[4px] border border-ink-300 grid place-items-center transition-colors group-hover:border-ink-700 overflow-hidden mt-0.5">
        <input
          ref={ref}
          id={fieldId}
          type="checkbox"
          {...rest}
          className="peer absolute inset-0 opacity-0 cursor-pointer"
        />
        <span className="absolute inset-0 scale-0 peer-checked:scale-100 transition-transform duration-200 ease-out bg-ink-900" />
        <svg
          className="relative z-10 w-[10px] h-[10px] text-white scale-0 peer-checked:scale-100 transition-transform duration-200 ease-out"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      {(label || hint) && (
        <span className="leading-tight text-[12.5px]">
          {label && <span className="block">{label}</span>}
          {hint && <span className="block text-ink-500 text-[11.5px] mt-0.5">{hint}</span>}
        </span>
      )}
    </label>
  );
});
